import React, { useState } from "react";
import { RazmerRow } from "../components/OSRazmerTable";
import { RNRazmerDispatchRow, RNRazmerDispatchStage, RNWorkOrderDoc } from "./useRNWorkOrder";

interface UseRNRazmerDispatchArgs {
  razmerRows: RazmerRow[];
  processGroupIdBySubprocess: Map<string, string>;
  processGroupOrder: string[];
  dispatchRazmerStage: (
    stage: RNRazmerDispatchStage,
    sentTo: string,
    rows: RNRazmerDispatchRow[]
  ) => Promise<RNWorkOrderDoc | null>;
  showToast: (message: string, type: "success" | "error") => void;
}

// Размерување dispatch (испрати припрема/пакување до соодветната фаза). Move-only out
// of RNWorkOrder.tsx — same state, same memo/callback deps, same behavior.
export const useRNRazmerDispatch = ({
  razmerRows,
  processGroupIdBySubprocess,
  processGroupOrder,
  dispatchRazmerStage,
  showToast,
}: UseRNRazmerDispatchArgs) => {
  const [dispatchModalStage, setDispatchModalStage] = useState<RNRazmerDispatchStage | null>(null);
  const [dispatchUserId, setDispatchUserId] = useState("");
  const [isDispatching, setIsDispatching] = useState(false);

  // Dispatch follows the same processDefinition.order as the RN presentation;
  // the first and second present process groups are preparation and packaging.
  const razmerDispatchGroupIds = React.useMemo(() => {
    const sectionIds: string[] = [];
    const seen = new Set<string>();
    razmerRows.forEach((row) => {
      const groupId = processGroupIdBySubprocess.get(row.subprocessId);
      if (!groupId || seen.has(groupId)) return;
      seen.add(groupId);
      sectionIds.push(groupId);
    });
    const rank = new Map(processGroupOrder.map((groupId, index) => [groupId, index]));
    sectionIds.sort((a, b) => (rank.get(a) ?? processGroupOrder.length) - (rank.get(b) ?? processGroupOrder.length));
    return {
      preparation: sectionIds[0] || "",
      packaging: sectionIds[1] || "",
    };
  }, [processGroupIdBySubprocess, processGroupOrder, razmerRows]);

  const razmerDispatchLabels = React.useMemo(() => ({
    preparation: "Размерување припрема",
    packaging: "Размерување пакување",
  }), []);

  const buildDispatchRows = React.useCallback((stage: RNRazmerDispatchStage): RNRazmerDispatchRow[] => {
    const groupId = razmerDispatchGroupIds[stage];
    if (!groupId) return [];
    return razmerRows
      .filter((row) => processGroupIdBySubprocess.get(row.subprocessId) === groupId)
      .filter((row) => {
        if (!row.itemId || !row.osRowId) return false;
        // Исти eligible правило како ✓ во OSRazmerTable — празен ред без кол. не влегува.
        return !!(row.requiredQty || "").trim() || !!(row.measuredQty || "").trim();
      })
      .map((row) => ({
        rowKey: row.osRowId || "",
        item: row.itemId,
        ident: row.ident,
        name: row.name,
        unitCode: row.unitCode,
        measuredQty: row.measuredQty || "",
        confirmed: !!row.confirmed,
        verified: !!(row.verifiedByName || row.verifiedAt),
        serialNumber: row.serialNumber || "",
        expiryDate: row.expiryDate || "",
        waste: row.waste || "",
        rastur: row.rastur || "",
      }));
  }, [processGroupIdBySubprocess, razmerDispatchGroupIds, razmerRows]);

  const getDispatchState = React.useCallback((stage: RNRazmerDispatchStage) => {
    const groupId = razmerDispatchGroupIds[stage];
    // Само редови што реално се размеруваат (имаат потребна или измерена кол.).
    // Ред без количина не може да се потврди — не смее да го блокира копчето.
    const rows = groupId
      ? razmerRows.filter((row) => {
          if (processGroupIdBySubprocess.get(row.subprocessId) !== groupId || !row.itemId) return false;
          return !!(row.requiredQty || "").trim() || !!(row.measuredQty || "").trim();
        })
      : [];
    // Праќање: сите eligible мора ✓ потврдени + Проверил штиклиран (verified).
    const ready = rows.length > 0 && rows.every(
      (row) =>
        !!row.confirmed
        && !!(row.measuredQty || "").trim()
        && !!(row.verifiedByName || row.verifiedAt)
    );
    return { rows, ready };
  }, [processGroupIdBySubprocess, razmerDispatchGroupIds, razmerRows]);

  const openDispatchModal = React.useCallback((stage: RNRazmerDispatchStage) => {
    setDispatchModalStage(stage);
    setDispatchUserId("");
  }, []);

  const handleDispatchSubmit = React.useCallback(async () => {
    if (!dispatchModalStage || !dispatchUserId) return;
    const rows = buildDispatchRows(dispatchModalStage);
    if (
      rows.length === 0
      || rows.some((row) => !row.confirmed || !(row.measuredQty || "").trim() || !row.verified)
    ) {
      showToast("Сите редови мора да се потврдени и проверени (Проверил) пред испраќање.", "error");
      return;
    }
    try {
      setIsDispatching(true);
      await dispatchRazmerStage(dispatchModalStage, dispatchUserId, rows);
      setDispatchModalStage(null);
      setDispatchUserId("");
      showToast("Размерувањето е испратено.", "success");
    } catch {
      // Hook shows the server message.
    } finally {
      setIsDispatching(false);
    }
  }, [buildDispatchRows, dispatchModalStage, dispatchRazmerStage, dispatchUserId, showToast]);

  return {
    dispatchModalStage,
    setDispatchModalStage,
    dispatchUserId,
    setDispatchUserId,
    isDispatching,
    razmerDispatchGroupIds,
    razmerDispatchLabels,
    buildDispatchRows,
    getDispatchState,
    openDispatchModal,
    handleDispatchSubmit,
  };
};
